import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  useMediaQuery,
  Chip,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DateRange } from "@mui/icons-material";
import api from "../../utils/api";

export const GROUP_NAME_BY_CODE = {
  "EH CH HILL": "Estate Homes",
  "EH EDGMT": "Estate Homes",
  SV: "Sommerset Villas",
  SC: "Southmont Cove",
  SW: "Sutton Walk",
  "VILLA HMS": "Villa Homes",
  WF: "Waterford",
  WW: "Wedgewood",
};

const norm = (v) => (v ?? "").toString().trim();
const canon = (v) => norm(v).replace(/\s+/g, " ").toUpperCase();

const normalizeMembershipCode = (v) => {
  const c = canon(v);
  if (/^(SV|SC|SW|WF|WW)\b/.test(c)) {
    return c.match(/^(SV|SC|SW|WF|WW)/)[1];
  }
  return c;
};

export const groupNameFor = (raw) => {
  const c = normalizeMembershipCode(raw);
  return GROUP_NAME_BY_CODE[c] || norm(raw);
};

export const groupIdFor = (raw) => canon(groupNameFor(raw));

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

/* ------------------------- Helper Functions ------------------------- */
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function offsetDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

const defaultStart = offsetDays(90);
const defaultEnd = todayISO();

const WorkOrdersExportPage = () => {
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const safeDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

  const getMemberFullName = (m) => {
    if (!m) return "";
    const first = (m.FamiliarName || m.FirstName || "").trim();
    const last = (m.LastName || "").trim();
    return `${first} ${last}`.trim();
  };

  /* ------------------------ Flattened / Normalized Rows ------------------------ */
  const normalizedData = useMemo(
    () =>
      filteredData.map((r) => {
        const rawCode = r.member?.ClubMembershipTypeCode || "";
        return {
          ...r,
          member_full_address:
            r.member?.AlternateAddress || r.member?.FullAddress || "",
          member_full_name: getMemberFullName(r.member),
          membership_type: rawCode ? groupNameFor(rawCode) : "",
          created_at_display: safeDate(r.created_at),
          resolved_at_display: safeDate(r.resolved_at),
          resolved_by_display: r.resolved_by_name || r.resolved_by || "",
        };
      }),
    [filteredData]
  );

  /* ------------------------ Date Presets ------------------------ */
  const presets = [
    {
      key: "30d",
      label: "Last 30 days",
      get: () => ({ start: offsetDays(30), end: todayISO() }),
    },
    {
      key: "90d",
      label: "Last 90 days",
      get: () => ({ start: offsetDays(90), end: todayISO() }),
    },
    {
      key: "mtd",
      label: "MTD",
      get: () => ({ start: firstOfMonthISO(), end: todayISO() }),
    },
    {
      key: "ytd",
      label: "YTD",
      get: () => ({
        start: `${new Date().getFullYear()}-01-01`,
        end: todayISO(),
      }),
    },
  ];

  /* ------------------------ Fetch Data ------------------------ */
  const buildMembershipTypeOptionsFromRows = (rows) => {
    const uniq = new Map();

    for (const r of rows || []) {
      const raw = r?.member?.ClubMembershipTypeCode;
      if (!raw) continue;

      const name = groupNameFor(raw);
      uniq.set(name, { value: name, label: name });
    }

    return Array.from(uniq.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  };

  const fetchMembershipTypeOptions = async () => {
    setMembershipTypeLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      if (selectedLocation) params.append("location_id", selectedLocation);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      params.append("includeMember", "1");

      const { data } = await api.get(`/getWorkOrdersReport?${params.toString()}`);
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(data || []));
    } catch (e) {
      console.error("membership type options (report) fetch error:", e);
      setMembershipTypeOptions([]);
    } finally {
      setMembershipTypeLoading(false);
    }
  };

  const handleViewComments = async (workOrderId) => {
    if (!workOrderId) return;
    setCommentsModalOpen(true);
    setCommentsLoading(true);
    setSelectedWorkOrder(workOrderId);

    try {
      const res = await api.get(`/getComments/${workOrderId}`);
      setSelectedComments(res.data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setSelectedComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const fetchCommentsForOrders = async (orders) => {
    const enriched = await Promise.all(
      orders.map(async (order) => {
        try {
          const res = await api.get(`/getComments/${order.order_id}`);
          const comments = res.data || [];
          const commentText = comments
            .map(
              (c) =>
                `${c.user_name} ${c.user_lastname}: "${c.comment_text}" (${new Date(
                  c.sent_at
                ).toLocaleString()})`
            )
            .join(" | ");
          return { ...order, comments_summary: commentText };
        } catch (err) {
          return { ...order, comments_summary: "" };
        }
      })
    );
    return enriched;
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [typesRes, subtypesRes, locationsRes, statusesRes] =
          await Promise.all([
            api.get("/workorder-types"),
            api.get("/workorder-subtypes"),
            api.get("/workorder-locations"),
            api.get("/statuses/workorders"),
          ]);
        setTypes(typesRes.data || []);
        setSubtypes(subtypesRes.data || []);
        setLocations(locationsRes.data || []);
        setStatuses(statusesRes.data || []);
      } catch (err) {
        console.error("Error fetching filter data:", err);
      }
    };

    fetchMembershipTypeOptions();
    fetchFilters();
  }, []);

  useEffect(() => {
    if (filteredData?.length) {
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(filteredData));
    }
  }, [filteredData]);

  /* ------------------------ Handlers ------------------------ */
  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedSubtype("");
    setSelectedLocation("");
  };

  const handleSubtypeChange = (e) => {
    setSelectedSubtype(e.target.value);
    setSelectedLocation("");
  };

  const handleReset = () => {
    setStatus("");
    setStartDate("");
    setEndDate("");
    setSelectedType("");
    setSelectedSubtype("");
    setSelectedLocation("");
    setPropertyId("");
    setMembershipType("");
    setFilteredData([]);
  };

  const handleSearch = async () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start Date must be before End Date.");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      if (selectedLocation) params.append("location_id", selectedLocation);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());

      if (membershipType.trim()) {
        const reverseMap = Object.fromEntries(
          Object.entries(GROUP_NAME_BY_CODE).map(([k, v]) => [v, k])
        );
        const code = reverseMap[membershipType.trim()] || membershipType.trim();
        params.append("club_membership_type_code", code);
      }

      params.append("includeMember", "1");

      const response = await api.get(`/getWorkOrdersReport?${params.toString()}`);
      let data = Array.isArray(response.data) ? response.data : [];

      data = await fetchCommentsForOrders(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error fetching filtered work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------ DataGrid Columns ------------------------ */
  const columns = useMemo(
    () => [
      { field: "order_id", headerName: "Order ID", width: 110 },
      { field: "property_id", headerName: "Member ID", width: 110 },
      { field: "membership_type", headerName: "Membership Type", width: 160 },
      { field: "member_full_name", headerName: "Member Name", width: 180 },
      {
        field: "member_full_address",
        headerName: "Member Full Address",
        width: 320,
        renderCell: (params) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }}
          >
            {params?.value || ""}
          </div>
        ),
      },
      { field: "status", headerName: "Status", width: 140 },
      { field: "type_name", headerName: "Type", width: 160 },
      { field: "subtype_name", headerName: "Subtype", width: 180 },
      { field: "location_name", headerName: "Location", width: 160 },
      {
        field: "description",
        headerName: "Description",
        width: 240,
        renderCell: (params) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }}
          >
            {params?.row?.description || ""}
          </div>
        ),
      },
      {
        field: "details",
        headerName: "Details",
        width: 260,
        renderCell: (params) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }}
          >
            {params?.row?.details || ""}
          </div>
        ),
      },
      {
        field: "opened_by",
        headerName: "Opened By",
        width: 170,
        renderCell: (params) => {
          const row = params?.row || {};
          return (
            row.opened_by_name ||
            row.opened_by_username ||
            (row.opened_by_user_id ? `No User found ${row.opened_by_user_id}` : "")
          );
        },
      },
      {
        field: "created_at_display",
        headerName: "Created At",
        width: 140,
      },
      {
        field: "resolved_at_display",
        headerName: "Resolved At",
        width: 140,
      },
      {
        field: "resolved_by_display",
        headerName: "Resolved By",
        width: 180,
      },
      {
        field: "comments",
        headerName: "Comments",
        width: 150,
        renderCell: (params) =>
          params?.row?.order_id ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleViewComments(params.row.order_id)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              View Comments
            </Button>
          ) : (
            "-"
          ),
      },
      {
        field: "comments_summary",
        headerName: "Comments Summary",
        width: 360,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
            {params.value || "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  const GRID_MIN_WIDTH =
    110 +
    110 +
    160 +
    180 +
    320 +
    140 +
    160 +
    180 +
    160 +
    240 +
    260 +
    170 +
    140 +
    140 +
    180 +
    150 +
    360;

  const initialColumnVisibility = useMemo(
    () =>
      isSmall
        ? {
            member_full_address: false,
            description: false,
            details: false,
            comments_summary: false,
          }
        : {
            member_full_address: true,
            description: true,
            details: true,
            comments_summary: true,
          },
    [isSmall]
  );

  return (
    <Box
      sx={{
        width: filteredData.length > 0 ? "65%" : "100%",
        transition: "width 0.3s ease",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Export Work Orders
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Search, review, and export work order records with comments and grouped membership data.
          </Typography>
        </Box>

        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Filters
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Refine the export by member, membership type, status, type, subtype,
              location, and date.
            </Typography>
          </Box>

          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Member/Property ID"
                type="text"
                InputLabelProps={{ shrink: true }}
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Membership Type</InputLabel>
                <Select
                  value={membershipType}
                  label="Membership Type"
                  onChange={(e) => setMembershipType(e.target.value)}
                  disabled={membershipTypeLoading}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {membershipTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.statusname}>
                      {s.statusname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {presets.map((p) => {
                  const { start, end } = p.get();
                  const active = startDate === start && endDate === end;
                  return (
                    <Chip
                      key={p.key}
                      icon={<DateRange />}
                      size="small"
                      label={p.label}
                      color={active ? "primary" : "default"}
                      variant={active ? "filled" : "outlined"}
                      onClick={() => {
                        setStartDate(start);
                        setEndDate(end);
                      }}
                      sx={{ borderRadius: "10px", fontWeight: 600 }}
                    />
                  );
                })}
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={selectedType}
                  label="Type"
                  onChange={handleTypeChange}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedType}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  value={selectedSubtype}
                  label="Subtype"
                  onChange={handleSubtypeChange}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {subtypes
                    .filter((s) => s.type_id === parseInt(selectedType || "-1", 10))
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.subtype_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedType}>
                <InputLabel>Location</InputLabel>
                <Select
                  value={selectedLocation}
                  label="Location"
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {locations
                    .filter((l) => l.type_id === parseInt(selectedType || "-1", 10))
                    .map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.location_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent={{ xs: "stretch", md: "flex-end" }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Reset Filters
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#275592",
                      boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                    },
                  }}
                >
                  Search
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 1.5, md: 2 },
          }}
        >
          <Box sx={{ mb: 2, px: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Results
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              {loading
                ? "Loading work orders..."
                : filteredData.length > 0
                ? `${filteredData.length} record${filteredData.length !== 1 ? "s" : ""} found`
                : "No results to display"}
            </Typography>
          </Box>

          <Box mt={1}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "#6b778c" }}>Loading...</Typography>
              </Box>
            ) : filteredData.length > 0 ? (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "rgba(255,255,255,0.98)",
                  }}
                >
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box sx={{ display: "inline-block", minWidth: `${GRID_MIN_WIDTH}px` }}>
                      <DataGrid
                        rows={normalizedData}
                        columns={columns}
                        getRowId={(r) =>
                          r.order_id ?? `${r.property_id}-${r.created_at}-${r.type_name}`
                        }
                        autoHeight
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 300 },
                          },
                        }}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                          },
                          columns: {
                            columnVisibilityModel: initialColumnVisibility,
                          },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowHeight={() => "auto"}
                        sx={{
                          border: "none",
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "rgba(47,99,173,0.04)",
                            color: "#223142",
                            fontWeight: 700,
                          },
                          "& .MuiDataGrid-cell": {
                            py: 1.25,
                            alignItems: "center",
                          },
                          "& .MuiDataGrid-toolbarContainer": {
                            p: 1.25,
                            borderBottom: "1px solid rgba(47,99,173,0.08)",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid rgba(47,99,173,0.08)",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>

                <Typography
                  variant="body2"
                  sx={{ mt: 2, textAlign: "left", fontWeight: 700, color: "#223142" }}
                >
                  Total Records: {filteredData.length}
                </Typography>
              </>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "#6b778c" }}>No results to display</Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Dialog
          open={commentsModalOpen}
          onClose={() => setCommentsModalOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: "18px",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
            Work Order {selectedWorkOrder ? `#${selectedWorkOrder}` : ""} Comments
          </DialogTitle>

          <DialogContent dividers>
            {commentsLoading ? (
              <Typography>Loading comments...</Typography>
            ) : selectedComments.length > 0 ? (
              selectedComments.map((c) => (
                <Box
                  key={c.comment_id}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    border: "1px solid rgba(47,99,173,0.10)",
                    borderRadius: "12px",
                    backgroundColor: c.is_internal ? "#f8fafc" : "white",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                  >
                    {c.user_name} {c.user_lastname}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line", color: "#445166" }}
                  >
                    {c.comment_text}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6b778c" }}>
                    {new Date(c.sent_at).toLocaleString()}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No comments available for this work order.
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setCommentsModalOpen(false)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default WorkOrdersExportPage;